import type {CampaignPopupState} from '../views/campaign-form-popup';
import type {IContactFormState} from '../views/contact-form';
import type {IContactPrefsState} from '../views/contact-prefs';
import type {IContentPrefsState} from '../views/content-prefs';
import type {IFleetFormState} from '../views/fleet-form';
import type {IFollowUpState} from '../views/follow-up';
import type {INewsletterFormState} from '../views/newsletter-form';
import type {NewsletterPopupState} from '../views/newsletter-form-popup';
import type {IReserveMySeatState} from '../views/reserve-my-seat';
import type {IReserveMySeatVIPState} from '../views/reserve-my-seat-vip';
import type {ISubscribeState} from '../views/subscribe-form';
import type {IUnsubscriptionFormState} from '../views/unsubscription';

export type CountryCode = (typeof availableCountries)[number];
export type LangCode = (typeof availableContactLanguages)[number];
export type PhoneCode = (typeof availablePhoneCodes)[number];
export type VariantType = (typeof availableVariants)[number];
export type ViewTypes = (typeof availableViews)[number];
export type Salutations = (typeof availableSalutations)[number];
export type SalutationLang = (typeof availableSalutationTranslations)[number];
export type AEMCFTypes = (typeof availableAEMCFs)[number];

export const availableCountries = [
  `AT`,
  `BE`,
  `CH`,
  `DE`,
  `ES`,
  `FR`,
  `GB`,
  `IT`,
  `NL`,
  `PT`,
  `SE`,
] as const;

export const availableContactLanguages = [
  `de`,
  `en`,
  `es`,
  `fr`,
  `it`,
  `nl`,
  `pt`,
  `sv`,
] as const;

export const availablePhoneCodes = [
  `+43`,
  `+32`,
  `+41`,
  `+49`,
  `+34`,
  `+33`,
  `+44`,
  `+39`,
  `+31`,
  `+351`,
  `+46`,
] as const;

export const availableSalutations = [
  `masculine`,
  `feminineMarried`,
  `feminineNeutral`,
  `feminineSingle`,
  `diverseMixed`,
  `diverseNone`,
  `individual`,
] as const;

export const availableSalutationTranslations = [
  `Mr.`,
  `Herr`,
  `Sr.`,
  `M.`,
  `Senhor`,
  `Dhr.`,
  `Sig.`,
  `Mrs.`,
  `Frau`,
  `Sra.`,
  `Mme.`,
  `Senhora`,
  `Mw.`,
  `Sig.ra.`,
  `Ms.`,
  `Miss`,
  `Srta.`,
  `Mlle.`,
  `Senhorita`,
  `Juf.`,
  `Sig.na.`,
  `diverse.mixed`,
  `diverse.none`,
  `individual`,
  `X`,
  `*`,
  `Mx.`,
  `Ne se prononçe pas`,
  `Preferisco non specificare`,
  `Frann`,
  `Mau`,
  `Ind.`,
  `Per.`,
  `Sx.`,
  `Pessoa`,
  `Fru`,
] as const;

export const salutationMap: {[k in Salutations]: SalutationLang[]} = {
  masculine: [`Mr.`, `Herr`, `Sr.`, `M.`, `Senhor`, `Dhr.`, `Sig.`],
  feminineMarried: [
    `Mrs.`,
    `Frau`,
    `Sra.`,
    `Mme.`,
    `Senhora`,
    `Mw.`,
    `Sig.ra.`,
    `Fru`,
  ],
  feminineSingle: [`Miss`, `Srta.`, `Mlle.`, `Senhorita`, `Juf.`, `Sig.na.`],
  feminineNeutral: [`Ms.`, `X`],
  diverseMixed: [`Mx.`, `Frann`, `Mau`, `Mx.`, `Sx.`, `X`],
  diverseNone: [
    `M.`,
    `X`,
    `*`,
    `Ne se prononçe pas`,
    `Preferisco non specificare`,
  ],
  individual: [`Ind.`, `Per.`, `Pessoa`],
};

export const languageMap: {[k in CountryCode]: LangCode[]} = {
  DE: [`de`],
  AT: [`de`],
  FR: [`fr`],
  BE: [`fr`, `nl`],
  IT: [`it`],
  ES: [`es`],
  NL: [`nl`],
  PT: [`pt`],
  CH: [`de`, `fr`, `it`],
  GB: [`en`],
  SE: [`sv`],
};

export const phoneMap = {
  DE: `+49`,
  AT: `+43`,
  FR: `+33`,
  BE: `+32`,
  IT: `+39`,
  ES: `+34`,
  NL: `+31`,
  PT: `+351`,
  CH: `+41`,
  GB: `+44`,
  SE: `+46`,
};

export const availableVariants = [
  `contact`,
  `newsletter`,
  `reservemyseat`,
  `reservemyseat_vip`,
  `unsubscription`,
  `fleet`,
  `subscribe`,
  `subscribe_popup`,
  `newsletter_popup`,
  `campaign_popup`,
] as const;

export const availableViews = [
  `contact-form`,
  `newsletter-form`,
  `reserve-my-seat`,
  `follow-up`,
  `contact-prefs`,
  `content-prefs`,
  `reserve-my-seat-vip`,
  `newsletter-form-popup`,
  `unsubscription`,
  `success`,
  `fleet-form`,
  `subscribe-form`,
  `subscribe-form-popup`,
  `campaign-form-popup`,
] as const;

export const availableAEMCFs = [
  `contact-ui`,
  `fleet-ui`,
  `newsletter-ui`,
  `newsletter-popup-ui`,
  `reserve-my-seat-ui`,
  `reserve-my-seat-vip-ui`,
  `unsubscribe-ui`,
  `subscribe-ui`,
  `campaign-popup-ui`,
] as const;

export type ViewsFetchingToPostForm = Exclude<ViewTypes, 'success'>;

export type IViewStateMapping<T = AdditionalViewStateProps> = Pick<
  {
    'contact-form': IContactFormState & T;
    'newsletter-form': INewsletterFormState & T;
    'newsletter-form-popup': NewsletterPopupState & T;
    'campaign-form-popup': CampaignPopupState & T;
    'reserve-my-seat': IReserveMySeatState & T;
    'follow-up': IFollowUpState & T;
    'contact-prefs': IContactPrefsState & T;
    'content-prefs': IContentPrefsState & T;
    'reserve-my-seat-vip': IReserveMySeatVIPState & T;
    'unsubscription': IUnsubscriptionFormState & T;
    'fleet-form': IFleetFormState & T;
    'subscribe-form': ISubscribeState & T;
    'subscribe-form-popup': ISubscribeState & T;
  },
  ViewsFetchingToPostForm
>;

export type AllViewStates = IViewStateMapping[ViewsFetchingToPostForm];

type AdditionalViewStateProps = {
  market: CountryCode;
  ecid?: string;
  CountryCode: CountryCode;
};

export type TranslationsProps = Partial<
  Record<LangCode, Record<string, string>>
>;

export const isLangCode = (lang: LangCode | string): lang is LangCode =>
  availableContactLanguages.includes(lang as LangCode);

export interface IViewConfig {
  component: (props?: any) => JSX.Element;
  tokenRequired: 'new' | 'required';
}
